/* ExerciseVideoModal — clickable exercise → Sora demo video (with polling). */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, Play, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExerciseVideoModal({ exercise, onClose }) {
  const [status, setStatus] = useState('starting');   // starting | generating | ready | failed
  const [videoB64, setVideoB64] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const tickRef = useRef(null);
  const startedAtRef = useRef(Date.now());

  useEffect(() => {
    const start = async () => {
      try {
        const { data } = await axios.post(
          `${API}/pt/exercise-video`,
          { name: exercise.name, description: exercise.how_to || exercise.tip || '' },
          { withCredentials: true },
        );
        if (data.status === 'ready' && data.video_b64) {
          setVideoB64(data.video_b64);
          setStatus('ready');
          return;
        }
        setStatus('generating');
        const videoId = data.id;
        pollRef.current = setInterval(async () => {
          try {
            const r = await axios.get(`${API}/pt/exercise-video/${videoId}`, { withCredentials: true });
            if (r.data.status === 'ready' && r.data.video_b64) {
              setVideoB64(r.data.video_b64);
              setStatus('ready');
              clearInterval(pollRef.current);
            } else if (r.data.status === 'failed') {
              setStatus('failed');
              clearInterval(pollRef.current);
            }
          } catch { /* keep polling */ }
        }, 4000);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Kunde inte starta video');
        setStatus('failed');
      }
    };
    start();
    tickRef.current = setInterval(() => {
      setElapsed(Math.round((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [exercise.name, exercise.how_to, exercise.tip]);

  const restart = () => {
    setStatus('starting');
    setVideoB64('');
    setElapsed(0);
    startedAtRef.current = Date.now();
    if (pollRef.current) clearInterval(pollRef.current);
    // Re-run effect by clearing state; simpler: just call the start again
    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/pt/exercise-video`,
          { name: exercise.name, description: exercise.how_to || '' },
          { withCredentials: true },
        );
        if (data.status === 'ready' && data.video_b64) {
          setVideoB64(data.video_b64);
          setStatus('ready');
          return;
        }
        setStatus('generating');
        const videoId = data.id;
        pollRef.current = setInterval(async () => {
          const r = await axios.get(`${API}/pt/exercise-video/${videoId}`, { withCredentials: true });
          if (r.data.status === 'ready' && r.data.video_b64) {
            setVideoB64(r.data.video_b64);
            setStatus('ready');
            clearInterval(pollRef.current);
          } else if (r.data.status === 'failed') {
            setStatus('failed');
            clearInterval(pollRef.current);
          }
        }, 4000);
      } catch { setStatus('failed'); }
    })();
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-lg w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="exercise-video-modal"
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div>
            <div className="text-[10px] uppercase font-bold text-fuchsia-700 tracking-wider">Demo · Sora 2</div>
            <div className="font-bold text-slate-900">{exercise.name}</div>
            {exercise.machine && <div className="text-[11px] text-fuchsia-600">🔧 {exercise.machine}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="exercise-video-close"
            className="text-slate-400 hover:text-slate-700"
            aria-label="Stäng"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="aspect-video bg-slate-950 flex items-center justify-center relative">
          {status === 'ready' && videoB64 ? (
            <video
              src={`data:video/mp4;base64,${videoB64}`}
              controls
              autoPlay
              loop
              className="w-full h-full object-contain"
              data-testid="exercise-video-player"
            />
          ) : status === 'failed' ? (
            <div className="text-center text-white/80 p-6">
              <p className="text-sm mb-3">Kunde inte generera videon 💔</p>
              <button
                type="button"
                onClick={restart}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs"
              >
                <RefreshCw className="w-3 h-3" /> Försök igen
              </button>
            </div>
          ) : (
            <div className="text-center text-white/80 p-6">
              <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin text-fuchsia-400" />
              <p className="text-sm">Nova spelar in demo…</p>
              <p className="text-xs opacity-60 mt-1">Detta tar 30-90 sekunder första gången ({elapsed}s förflutet)</p>
            </div>
          )}
        </div>

        {exercise.how_to && (
          <div className="p-4 text-sm text-slate-700">
            <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1 flex items-center gap-1">
              <Play className="w-3 h-3" /> Så här gör du
            </div>
            {exercise.how_to}
          </div>
        )}
      </div>
    </div>
  );
}
